import React, { useCallback, useRef, useMemo, useEffect } from 'react';
import { useForm, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { MetaAdCampaignObjective } from '@frontend/gql/social/graphql';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Label } from '@frontend/shadcn/components/ui/label';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import { Form, FormField, FormItem, FormControl, FormMessage } from '@frontend/shadcn/components/ui/form';

// Import shared components from Analytics for selection
import { MembersSelect, MembersOptions } from '../../Analytics/components/filters-form/members-select';
import { Member } from '../../Analytics/components/filters-form/types';

// Import the new filter components
import { AdAccountsOptions } from './ad-accounts-select';
import { AdCampaignsAndSetsOptions } from './ad-campaigns-and-sets-select';
import { AdObjectivesOptions } from './ad-objectives-select';

export const adsFiltersFormSchema = z.object({
  adAccountIds: z.array(z.string()).default([]),
  adCampaignAndSetIds: z.array(z.string()).default([]),
  adObjectiveIds: z.array(z.string()).default([]),
  memberIds: z.array(z.string()).default([]),
  compareWithPartnershipAds: z.boolean().default(false),
});

export type AdsFiltersFormData = z.infer<typeof adsFiltersFormSchema>;

interface AdAccount {
  id: string;
  name: string;
}

interface AdCampaign {
  id: string;
  name: string;
  adSets?: AdSet[];
}

interface AdSet {
  id: string;
  name: string;
}

interface AdObjective {
  id: string;
  name: string;
  objective: MetaAdCampaignObjective;
}

interface AdsFiltersFormProps {
  adAccounts: AdAccount[];
  adCampaigns: AdCampaign[];
  adObjectives: AdObjective[];
  isFetchingAdAccounts: boolean;
  isFetchingAdsData: boolean;
  members: Member[];
  setMemberSearchTerm: (term: string) => void;
  isFetchingMembers: boolean;
  defaultValues?: Partial<AdsFiltersFormData>;
  onSubmit: (data: AdsFiltersFormData) => void;
  onCancel: () => void;
}

export const AdsFiltersForm: React.FC<AdsFiltersFormProps> = ({
  adAccounts,
  adCampaigns,
  adObjectives,
  isFetchingAdAccounts,
  isFetchingAdsData,
  members,
  setMemberSearchTerm,
  isFetchingMembers,
  defaultValues,
  onSubmit,
  onCancel,
}) => {
  const form = useForm<AdsFiltersFormData>({
    resolver: zodResolver(adsFiltersFormSchema),
    defaultValues: useMemo(() => ({
      adAccountIds: defaultValues?.adAccountIds || [],
      adCampaignAndSetIds: defaultValues?.adCampaignAndSetIds || [],
      adObjectiveIds: defaultValues?.adObjectiveIds || [],
      memberIds: defaultValues?.memberIds || [],
      compareWithPartnershipAds: defaultValues?.compareWithPartnershipAds || false,
    }), [defaultValues]),
  });

  // Reset form values when defaultValues change
  useEffect(() => {
    const newValues = {
      adAccountIds: defaultValues?.adAccountIds || [],
      adCampaignAndSetIds: defaultValues?.adCampaignAndSetIds || [],
      adObjectiveIds: defaultValues?.adObjectiveIds || [],
      memberIds: defaultValues?.memberIds || [],
      compareWithPartnershipAds: defaultValues?.compareWithPartnershipAds || false,
    };
    form.reset(newValues);
  }, [defaultValues, form]);

  // Data is now provided via props from the useAdsFilterFormData hook

  // Debounce timer reference for search
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search handler for members
  const handleMemberSearch = useCallback((value: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Immediately set empty value without debounce to reset members
    if (!value || value.length === 0) {
      setMemberSearchTerm('');
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      setMemberSearchTerm(value);
    }, 300);
  }, [setMemberSearchTerm]);

  const handleSubmit = (data: AdsFiltersFormData) => {
    onSubmit(data);
  };

  const handleError = (errors: FieldErrors<AdsFiltersFormData>) => {
    console.error('Ads filter form validation errors:', errors);
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit, handleError)} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <Form {...form}>
          {/* Ad Accounts Filter */}
          <div className="space-y-2 mb-2 mt-4">
            <Label>Ad Accounts</Label>
            <FormField
              control={form.control}
              name="adAccountIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AdAccountsOptions
                      adAccounts={adAccounts}
                      isLoading={isFetchingAdAccounts}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Ad Campaigns and Ad Sets Filter */}
          <div className="space-y-2 mb-2">
            <Label>Ad Campaigns and Ad Sets</Label>
            <FormField
              control={form.control}
              name="adCampaignAndSetIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AdCampaignsAndSetsOptions
                      adCampaigns={adCampaigns}
                      isLoading={isFetchingAdsData}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Ads Objectives Filter */}
          <div className="space-y-2 mb-2">
            <Label>Ads Objectives</Label>
            <FormField
              control={form.control}
              name="adObjectiveIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AdObjectivesOptions
                      adObjectives={adObjectives}
                      isLoading={isFetchingAdsData}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Member Filter */}
          <div className="space-y-2 mb-2">
            <Label>Member</Label>
            <FormField
              control={form.control}
              name="memberIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <MembersSelect>
                      <MembersOptions
                        members={members || []}
                        isLoading={isFetchingMembers}
                        value={field.value}
                        onChange={field.onChange}
                        onSearch={handleMemberSearch}
                      />
                    </MembersSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Partnership Ads Comparison Switch */}
          <div className="space-y-2 mb-2">
            <FormField
              control={form.control}
              name="compareWithPartnershipAds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="compareWithPartnershipAds"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="data-[state=checked]:bg-primary"
                        disabled
                      />
                      <Label
                        htmlFor="compareWithPartnershipAds"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Compare Ads with "Partnership Ads" Toggle on in Meta for Aspire's content
                      </Label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>
      </div>

      <div className="flex justify-start gap-2 p-4 mt-auto mb-4 border-t bg-background mx-[-1.5rem]">
        <Button type="submit">Apply</Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
